import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, Trash2, Eye, Search, ChevronDown, ChevronRight,
         Building2, Calendar, FileText, DollarSign, Upload, Download,
         Paperclip, AlertTriangle, ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ── Constants ──────────────────────────────────────────────────────
// One fund list for both Projects and Tenders so fund-wise reports match
const FUND_TYPES    = ['Government', 'MLA ADS', 'MGNREGS', 'LSG', 'LSGD', 'PWD', 'KSEB', 'KWA', 'BPCL', 'State Fund', 'Central Fund', 'Private', 'Other'];
const EMD_STATUS    = ['Pending', 'Submitted', 'Returned', 'Forfeited'];
const SD_STATUS     = ['Pending', 'Submitted', 'Returned', 'Forfeited'];
const RA_STATUS     = ['Pending', 'Refunded', 'Partial'];
const PROJ_STATUS   = ['Active', 'Completed', 'On Hold', 'Cancelled'];
const ORGANIZATIONS = ['LSGD','KWA','KSEB','BPCL','PWD','Corporation','Panchayath','Other'];
const DISTRICTS_LIST = ['THIRUVANANTHAPURAM','KOLLAM','PATHANAMTHITTA','ALAPPUZHA','KOTTAYAM',
                        'IDUKKI','ERNAKULAM','THRISSUR','PALAKKAD','MALAPPURAM',
                        'KOZHIKODE','WAYANAD','KANNUR','KASARAGOD'];
const PARTICIP_OPTS = ['YES','NO','Applied','Shortlisted','Awarded','Rejected'];
const STATUS_OPTS   = ['Open','Closed','Awarded','Cancelled','Rejected','Under Review','Pending'];
const YEARS         = (() => { const y = new Date().getFullYear(); const a = []; for (let i = 2021; i <= y + 1; i++) a.push(i); return a; })();
const OFFICES       = [['AE Office','ae'],['AXE Office','axe'],['EE Office','ee'],['FO Office','fo']];
const BUCKET        = 'civil-docs';
const STUCK_DAYS    = 15;   // bill waiting at one office longer than this raises an alert
const SOON_DAYS     = 7;    // tender closing within this many days raises a flag
const PAGE_SIZE     = 50;
const INR = v => Number(v||0).toLocaleString('en-IN', {minimumFractionDigits:2, maximumFractionDigits:2});

// PostgREST returns max 1000 rows per request — page through everything
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
const pad2 = n => String(n).padStart(2, '0');
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; };
// Excel/CSV cell -> 'YYYY-MM-DD' without UTC shift. Handles Date, serial, ISO and dd/mm/yyyy text.
function parseXlDate(v) {
  if (v === '' || v == null) return null;
  if (v instanceof Date) { if (isNaN(v)) return null; const d = new Date(v.getTime() + 12*3600*1000); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; }
  if (typeof v === 'number') { if (v < 20000) return null; const d = new Date(Math.round((v-25569)*86400*1000)); return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth()+1)}-${pad2(d.getUTCDate())}`; }
  const t = String(v).trim();
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m) { const y = m[3].length === 2 ? '20' + m[3] : m[3]; return `${y}-${pad2(m[2])}-${pad2(m[1])}`; }
  return null;
}
const hasData = o => Object.values(o || {}).some(v => v !== '' && v != null);
const num = v => (v === '' || v == null || isNaN(Number(v))) ? 0 : Number(v);
// true only for a real non-zero amount (so React never prints a stray "0")
const has = v => v != null && v !== '' && Number(v) !== 0;
const dayDiff = iso => {
  if (!iso) return 0;
  const a = new Date(localToday() + 'T00:00:00Z'), b = new Date(String(iso).slice(0,10) + 'T00:00:00Z');
  return Math.round((a - b) / 86400000);
};
const fmtStamp = s => { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? '—' : d.toLocaleString('en-IN'); };
const canon = (v, list) => { const t = String(v||'').trim(); if (!t) return ''; return list.find(x => x.toLowerCase() === t.toLowerCase()) || t; };

// ── Bill maths ────────────────────────────────────────────────────
const billTotal = b => has(b.total_amount) ? num(b.total_amount) : num(b.bill_amount) + num(b.gst_amount);
// Outstanding = total - received - TDS - retention (retention is tracked separately as RA)
const billOutstanding = b => { const t = billTotal(b); return t > 0 ? Math.max(0, t - num(b.amount_received) - num(b.tds_deduction) - num(b.retention_amount)) : 0; };
function projTotals(p) {
  const bills = p.bills || [];
  const sum = f => bills.reduce((s,b) => s + f(b), 0);
  return {
    count: bills.length,
    billed: sum(b => num(b.bill_amount)), gst: sum(b => num(b.gst_amount)), total: sum(billTotal),
    received: sum(b => num(b.amount_received)), tds: sum(b => num(b.tds_deduction)),
    ret: sum(b => num(b.retention_amount)), out: sum(billOutstanding),
  };
}
// Where a bill currently sits in the AE → AXE → EE → FO chain
function billStage(b) {
  let last = null;
  for (const [label, k] of OFFICES) if (b['bill_generated_'+k] || b['bill_cleared_'+k]) last = [label, k];
  if (!last) return null;
  const [label, k] = last, short = label.split(' ')[0];
  if (b['bill_cleared_'+k]) return { text: `Cleared ${short}`, days: 0, pending: false };
  return { text: `At ${short}`, days: dayDiff(b['bill_generated_'+k]), pending: true };
}

// ── Supabase helpers ──────────────────────────────────────────────
const isMissingCol = e => !!e && /column|schema cache/i.test(e.message||'') && /(does not exist|could not find)/i.test(e.message||'');
async function whoAmI() {
  try { const { data } = await supabase.auth.getUser(); return data?.user?.email || null; } catch (e) { return null; }
}
// Saves core + extra fields; if the new columns are not in the DB yet it saves the core fields only
async function saveRow(table, id, core, extra) {
  const run = body => id
    ? supabase.from(table).update(body).eq('id', id).select('id')
    : supabase.from(table).insert(body).select('id');
  let r = await run({ ...core, ...extra });
  if (r.error && isMissingCol(r.error)) {
    r = await run(core);
    if (!r.error) toast('Saved, but the new fields were skipped — run civil_upgrade.sql in Supabase', { icon: '⚠️', duration: 7000 });
  }
  if (!r.error && id && !(r.data || []).length) r.error = { message: 'Not saved — you may not have permission, or the record was removed' };
  return r;
}
async function openAttachment(a) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(a.path, 300);
  if (error) { toast.error('Cannot open file: ' + error.message); return; }
  window.open(data.signedUrl, '_blank', 'noopener');
}
async function removeFiles(list) {
  const paths = (list || []).map(a => a.path).filter(Boolean);
  if (paths.length) { try { await supabase.storage.from(BUCKET).remove(paths); } catch (e) { /* ignore */ } }
}

// ── Empty templates ───────────────────────────────────────────────
const emptyBill = () => ({
  bill_generated_ae:'', bill_cleared_ae:'',
  bill_amount:'', gst_amount:'', total_amount:'', bill_no:'',
  bill_generated_axe:'', bill_cleared_axe:'',
  bill_generated_ee:'', bill_cleared_ee:'',
  bill_generated_fo:'', bill_cleared_fo:'',
  amount_received:'', amount_received_date:'',
  tds_deduction:'', retention_amount:'',
  ra_refund_expected:'', ra_refund_status:'', remarks:'',
});
const emptyTOC = () => ({ requested_date:'', approved_date:'', validity_period:'', remarks:'' });
const emptyRE  = () => ({ generated_date:'', approved_date:'', remarks:'' });
const emptyLetter = () => ({ submission_date:'', reason:'', remarks:'' });
const BILL_NUM_KEYS = ['bill_amount','gst_amount','total_amount','amount_received','tds_deduction','retention_amount'];

const emptyProject = () => ({
  project_name:'', fund:'', location:'', estimated_amount:'',
  estimated_completion_date:'', status:'Active',
  tender_form_fee:'', emd_fee:'', emd_status:'Pending',
  security_deposit:'', sd_return_expected_date:'', sd_return_status:'Pending',
  project_awarded_date:'', site_handover_date:'',
  actual_start_date:'', actual_completion_date:'',
  bills: [emptyBill()], tocs: [emptyTOC()], revisions: [emptyRE()], letters: [emptyLetter()],
  general_remarks:'', tender_id:'', attachments: [],
});
const emptyTender = () => ({
  tender_ref:'', organization:'LSGD', district:'',
  project_location:'', work_description:'', nature_of_fund:'',
  tender_value:'', tender_published_date:'', tender_closing_date:'',
  bid_opening_date:'', work_completion:'', participation:'NO',
  tender_submitted_date:'', tender_status:'',
  tender_form_fee:'', emd_fee:'', security_amount:'', remarks:'', attachments: [],
});

// ── Small UI helpers ──────────────────────────────────────────────
const SH = ({title, color='var(--accent)', icon:Icon, mt=14}) => (
  <div style={{marginTop:mt, marginBottom:10, padding:'7px 14px',
    background:color+'18', borderLeft:`3px solid ${color}`,
    borderRadius:'0 8px 8px 0', fontWeight:700, fontSize:12, color,
    display:'flex', alignItems:'center', gap:8}}>
    {Icon && <Icon size={14}/>}{title}
  </div>
);
const FG = ({label, children, full, style={}}) => (
  <div className={`form-group${full?' full':''}`} style={style}>
    <label style={{fontSize:11}}>{label}</label>{children}
  </div>
);
const KV = ({label, children}) => (
  <div style={{display:'flex',gap:12,padding:'5px 0',borderBottom:'1px solid var(--border)'}}>
    <div style={{width:150,fontWeight:600,fontSize:12,color:'var(--text2)',flexShrink:0}}>{label}</div>
    <div style={{fontSize:13,minWidth:0,wordBreak:'break-word'}}>{children}</div>
  </div>
);

// Upload / list / open attachments (stored in the private "civil-docs" bucket)
function Attachments({ value, onChange, folder, editable }) {
  const [busy, setBusy] = useState(false);
  const list = value || [];
  async function onPick(e) {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      if (f.size > 10*1024*1024) { toast.error(`${f.name}: larger than 10 MB`); continue; }
      const path = `${folder}/${Date.now()}_${f.name.replace(/[^\w.-]+/g,'_')}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, f, { contentType: f.type || undefined });
      if (error) { toast.error(`${f.name}: ${error.message}`); continue; }
      added.push({ path, name: f.name, size: f.size, at: new Date().toISOString() });
    }
    if (added.length) onChange([...list, ...added]);
    setBusy(false);
  }
  return (
    <div>
      {list.length === 0 && <div style={{fontSize:12,color:'var(--text3)',marginBottom:6}}>No files attached</div>}
      {list.map((a,i) => (
        <div key={a.path||i} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',fontSize:12}}>
          <Paperclip size={12} style={{color:'var(--text3)'}}/>
          <a href="#open" onClick={e=>{e.preventDefault();openAttachment(a);}} style={{color:'var(--accent)',fontWeight:600}}>{a.name}</a>
          <span style={{color:'var(--text3)'}}>{a.size ? (a.size/1024).toFixed(0)+' KB' : ''}</span>
          {editable && <button type="button" className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} title="Remove" onClick={()=>onChange(list.filter((_,j)=>j!==i))}><X size={12}/></button>}
        </div>
      ))}
      {editable && (
        <label className="btn btn-secondary btn-sm" style={{display:'inline-flex',gap:6,alignItems:'center',cursor:busy?'wait':'pointer',marginTop:4}}>
          <Upload size={13}/> {busy ? 'Uploading…' : 'Attach files (max 10 MB each)'}
          <input type="file" multiple style={{display:'none'}} disabled={busy} onChange={onPick}/>
        </label>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════
export default function CivilConstruction() {
  const { canView, canEdit } = useAuth();
  const showP = !!canView('civil_projects');
  const showT = !!canView('civil_tenders');
  const [tab, setTab] = useState(showP ? 'projects' : 'tenders');
  const [prefill, setPrefill] = useState(null);

  // land on a tab the user is allowed to see
  useEffect(() => {
    if (tab === 'projects' && !showP && showT) setTab('tenders');
    if (tab === 'tenders' && !showT && showP) setTab('projects');
  }, [tab, showP, showT]);

  const canEditCivil = key => !!(canEdit(key) || canEdit('civil'));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Civil Construction</h1>
          <p className="page-subtitle">Project database, bills, milestones and site tracking</p>
        </div>
      </div>
      <div className="tabs" style={{marginBottom:20}}>
        {showP && <div className={`tab ${tab==='projects'?'active':''}`} onClick={()=>setTab('projects')}>
          <Building2 size={14} style={{marginRight:4}}/> Projects DB
        </div>}
        {showT && <div className={`tab ${tab==='tenders'?'active':''}`} onClick={()=>setTab('tenders')}>
          <FileText size={14} style={{marginRight:4}}/> Tender DB
        </div>}
      </div>
      {tab==='projects' && showP && <ProjectsDB editable={canEditCivil('civil_projects')} prefill={prefill} onPrefillUsed={()=>setPrefill(null)}/>}
      {tab==='tenders'  && showT && <TenderDB editable={canEditCivil('civil_tenders')}
        canConvert={showP && canEditCivil('civil_projects')} onConvert={t=>{ setPrefill(t); setTab('projects'); }}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PROJECT VIEW (read-only popup)
// ═══════════════════════════════════════════════════════════════════
function ProjectView({ p, onClose, onEdit, editable }) {
  const t = projTotals(p);
  const bills = p.bills || [];
  const money = v => has(v) ? '₹' + INR(v) : '—';
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:900,maxHeight:'94vh',overflowY:'auto'}}>
        <div className="modal-header">
          <span className="modal-title">🏗️ {p.project_name}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <SH title="Project Details" icon={Building2} mt={0}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',columnGap:24}}>
            <div>
              <KV label="Status">{p.status||'—'}</KV>
              <KV label="Fund / Client">{p.fund||'—'}</KV>
              <KV label="Location">{p.location||'—'}</KV>
              <KV label="Estimated Amount">{money(p.estimated_amount)}</KV>
              <KV label="Est. Completion">{fmtDMY(p.estimated_completion_date)||'—'}</KV>
              <KV label="Tender Form Fee">{money(p.tender_form_fee)}</KV>
              <KV label="EMD Fee">{money(p.emd_fee)} <span style={{color:'var(--text3)'}}>({p.emd_status||'—'})</span></KV>
            </div>
            <div>
              <KV label="Security Deposit">{money(p.security_deposit)} <span style={{color:'var(--text3)'}}>({p.sd_return_status||'—'})</span></KV>
              <KV label="SD Return Expected">{fmtDMY(p.sd_return_expected_date)||'—'}</KV>
              <KV label="Awarded">{fmtDMY(p.project_awarded_date)||'—'}</KV>
              <KV label="Site Handover">{fmtDMY(p.site_handover_date)||'—'}</KV>
              <KV label="Started">{fmtDMY(p.actual_start_date)||'—'}</KV>
              <KV label="Completed">{fmtDMY(p.actual_completion_date)||'—'}</KV>
              <KV label="Linked Tender">{p.tender_id ? `Tender record #${p.tender_id}` : '—'}</KV>
            </div>
          </div>

          {bills.length>0&&(<>
            <SH title={`Bills (${bills.length}) — Outstanding ₹${INR(t.out)}`} color="#16a34a" icon={FileText}/>
            {bills.map((b,i)=>{
              const st = billStage(b);
              return (
                <div key={i} style={{border:'1px solid var(--border)',borderRadius:10,padding:'10px 14px',marginBottom:8,background:'var(--bg3)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:8,fontSize:12,marginBottom:6}}>
                    <b style={{color:'var(--green)'}}>Bill #{i+1}{b.bill_no?` — ${b.bill_no}`:''}</b>
                    <span>Total <b>₹{INR(billTotal(b))}</b> · Received <b style={{color:'var(--green)'}}>₹{INR(b.amount_received)}</b>{b.amount_received_date?` (${fmtDMY(b.amount_received_date)})`:''} · TDS ₹{INR(b.tds_deduction)} · Retention ₹{INR(b.retention_amount)} · <b style={{color:billOutstanding(b)>0?'var(--red)':'var(--green)'}}>Due ₹{INR(billOutstanding(b))}</b></span>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,fontSize:11}}>
                    {OFFICES.map(([label,k])=>(
                      <div key={k} style={{background:'var(--surface)',borderRadius:6,padding:'6px 8px'}}>
                        <div style={{fontWeight:700,color:'var(--text3)',fontSize:10}}>{label}</div>
                        <div>Generated: {fmtDMY(b['bill_generated_'+k])||'—'}</div>
                        <div>Cleared: {fmtDMY(b['bill_cleared_'+k])||'—'}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:11,marginTop:6,color:'var(--text2)'}}>
                    {st ? <>Stage: <b style={{color:st.pending&&st.days>STUCK_DAYS?'var(--red)':undefined}}>{st.text}{st.pending?` (${st.days} days)`:''}</b> · </> : null}
                    RA status: {b.ra_refund_status||'—'}{b.ra_refund_expected?` · RA expected ${fmtDMY(b.ra_refund_expected)}`:''}
                    {b.remarks?` · ${b.remarks}`:''}
                  </div>
                </div>
              );
            })}
          </>)}

          {(p.tocs||[]).some(hasData)&&(<>
            <SH title="TOC Extensions" color="#d97706"/>
            {(p.tocs||[]).filter(hasData).map((x,i)=>(
              <div key={i} style={{fontSize:12,marginBottom:3}}><b>TOC #{i+1}:</b> requested {fmtDMY(x.requested_date)||'—'} → approved {fmtDMY(x.approved_date)||'Pending'}{x.validity_period?` · ${x.validity_period}`:''}{x.remarks?` · ${x.remarks}`:''}</div>
            ))}
          </>)}
          {(p.revisions||[]).some(hasData)&&(<>
            <SH title="Revisions (RE)" color="#0891b2"/>
            {(p.revisions||[]).filter(hasData).map((x,i)=>(
              <div key={i} style={{fontSize:12,marginBottom:3}}><b>RE #{i+1}:</b> generated {fmtDMY(x.generated_date)||'—'} → approved {fmtDMY(x.approved_date)||'Pending'}{x.remarks?` · ${x.remarks}`:''}</div>
            ))}
          </>)}
          {(p.letters||[]).some(hasData)&&(<>
            <SH title="Letter Submissions" color="#dc2626"/>
            {(p.letters||[]).filter(hasData).map((x,i)=>(
              <div key={i} style={{fontSize:12,marginBottom:3}}><b>#{i+1}:</b> {fmtDMY(x.submission_date)||'—'} — {x.reason||'—'}{x.remarks?` · ${x.remarks}`:''}</div>
            ))}
          </>)}

          {p.general_remarks&&(<><SH title="General Remarks" color="#64748b"/><div style={{fontSize:13,whiteSpace:'pre-wrap'}}>{p.general_remarks}</div></>)}

          <SH title="Attachments" color="#475569" icon={Paperclip}/>
          <Attachments value={p.attachments} editable={false} folder=""/>

          <div style={{marginTop:14,fontSize:11,color:'var(--text3)'}}>
            Created {fmtStamp(p.created_at)}{p.created_by?` by ${p.created_by}`:''} · Last updated {fmtStamp(p.updated_at)}{p.updated_by?` by ${p.updated_by}`:''}
          </div>
        </div>
        <div className="modal-footer">
          {editable&&<button className="btn btn-secondary" onClick={()=>{onClose();onEdit(p);}}>Edit</button>}
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PROJECTS DB
// ═══════════════════════════════════════════════════════════════════
function ProjectsDB({ editable, prefill, onPrefillUsed }) {
  const [projects, setProjects]   = useState([]);
  const [expanded, setExpanded]   = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showView,  setShowView]  = useState(null);
  const [editing,  setEditing]    = useState(null);
  const [saving,   setSaving]     = useState(false);
  const [search,   setSearch]     = useState('');
  const [fStatus,  setFStatus]    = useState('');
  const [fFund,    setFFund]      = useState('');
  const [fDue,     setFDue]       = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [allAlerts,  setAllAlerts]  = useState(false);
  const [uploadKey, setUploadKey] = useState('');
  const [form, setForm]           = useState(emptyProject);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{ load(); },[]);
  useLiveRefresh(['civil_projects'], () => load(), true, 1500);
  async function load() {
    try { setProjects(await fetchAllPaged(() => supabase.from('civil_projects').select('*').order('created_at',{ascending:false}).order('id'))); }
    catch (e) { toast.error('Could not load projects: ' + e.message); }
  }

  // Tender → Project hand-over
  useEffect(()=>{
    if (!prefill) return;
    const t = prefill;
    setEditing(null);
    setUploadKey('new_' + Date.now());
    setForm({ ...emptyProject(),
      project_name: (t.work_description||'').slice(0,250),
      fund: FUND_TYPES.includes(t.nature_of_fund) ? t.nature_of_fund : (t.nature_of_fund ? 'Other' : ''),
      location: t.project_location||'',
      estimated_amount: has(t.tender_value) ? String(t.tender_value) : '',
      tender_form_fee: has(t.tender_form_fee ?? t.tender_fee) ? String(t.tender_form_fee ?? t.tender_fee) : '',
      emd_fee: has(t.emd_fee ?? t.emd) ? String(t.emd_fee ?? t.emd) : '',
      security_deposit: has(t.security_amount) ? String(t.security_amount) : '',
      tender_id: String(t.id),
      general_remarks: `Created from tender ${t.tender_ref||('#'+t.id)}`,
      attachments: [],
    });
    setShowModal(true);
    if (onPrefillUsed) onPrefillUsed();
  },[prefill]);

  // ── Save ────────────────────────────────────────────────────────
  async function save() {
    if (!form.project_name.trim()) { toast.error('Project name required'); return; }
    if (['estimated_amount','tender_form_fee','emd_fee','security_deposit'].some(k=>num(form[k])<0)) { toast.error('Amounts cannot be negative'); return; }
    if (form.actual_start_date && form.actual_completion_date && form.actual_completion_date < form.actual_start_date) { toast.error('Completion date is before the start date'); return; }
    for (let i=0;i<form.bills.length;i++) {
      const b = form.bills[i];
      if (!hasData(b)) continue;
      if (BILL_NUM_KEYS.some(k=>num(b[k])<0)) { toast.error(`Bill #${i+1}: amounts cannot be negative`); return; }
      const t = billTotal(b), taken = num(b.amount_received)+num(b.tds_deduction)+num(b.retention_amount);
      if (t > 0 && taken > t + 0.5 && !window.confirm(`Bill #${i+1}: received + TDS + retention (₹${INR(taken)}) is more than the bill total (₹${INR(t)}). Save anyway?`)) return;
      if (t === 0 && num(b.amount_received) > 0 && !window.confirm(`Bill #${i+1} has an amount received but no bill amount. Save anyway?`)) return;
    }
    setSaving(true);
    const n = v => v!==''&&v!=null ? Number(v) : null;
    const d = v => v&&String(v).trim() ? v : null;
    const cleanBill = b => {
      const o = { ...b };
      BILL_NUM_KEYS.forEach(k => { o[k] = (b[k]===''||b[k]==null) ? null : Number(b[k]); });
      if (o.total_amount == null && (o.bill_amount != null || o.gst_amount != null)) o.total_amount = Math.round((num(o.bill_amount)+num(o.gst_amount))*100)/100;
      return o;
    };
    const core = {
      project_name: form.project_name.trim(), fund: form.fund, location: form.location,
      estimated_amount: n(form.estimated_amount),
      estimated_completion_date: d(form.estimated_completion_date),
      status: form.status,
      tender_form_fee: n(form.tender_form_fee), emd_fee: n(form.emd_fee),
      emd_status: form.emd_status,
      security_deposit: n(form.security_deposit),
      sd_return_expected_date: d(form.sd_return_expected_date),
      sd_return_status: form.sd_return_status,
      project_awarded_date: d(form.project_awarded_date),
      site_handover_date: d(form.site_handover_date),
      actual_start_date: d(form.actual_start_date),
      actual_completion_date: d(form.actual_completion_date),
      bills: (form.bills||[]).filter(hasData).map(cleanBill),
      tocs: (form.tocs||[]).filter(hasData),
      revisions: (form.revisions||[]).filter(hasData),
      letters: (form.letters||[]).filter(hasData),
      general_remarks: form.general_remarks,
    };
    const me = await whoAmI();
    const extra = { attachments: form.attachments||[], tender_id: form.tender_id||null, updated_by: me, updated_at: new Date().toISOString(),
                    ...(editing ? {} : { created_by: me }) };
    const { error } = await saveRow('civil_projects', editing, core, extra);
    if (error) toast.error(error.message);
    else {
      if (!editing && form.tender_id) {
        await supabase.from('civil_tenders').update({ tender_status:'Awarded', participation:'Awarded' }).eq('id', form.tender_id);
      }
      toast.success(editing?'Project updated ✅':'Project saved ✅'); setShowModal(false); load();
    }
    setSaving(false);
  }

  function openNew()  { setEditing(null); setUploadKey('new_'+Date.now()); setForm(emptyProject()); setShowModal(true); }
  function openEdit(p){
    setEditing(p.id);
    setUploadKey(String(p.id));
    const s = v => v!=null ? String(v) : '';
    setForm({
      project_name:p.project_name||'', fund:p.fund||'', location:p.location||'',
      estimated_amount:s(p.estimated_amount),
      estimated_completion_date:p.estimated_completion_date||'', status:p.status||'Active',
      tender_form_fee:s(p.tender_form_fee),
      emd_fee:s(p.emd_fee), emd_status:p.emd_status||'Pending',
      security_deposit:s(p.security_deposit),
      sd_return_expected_date:p.sd_return_expected_date||'', sd_return_status:p.sd_return_status||'Pending',
      project_awarded_date:p.project_awarded_date||'', site_handover_date:p.site_handover_date||'',
      actual_start_date:p.actual_start_date||'', actual_completion_date:p.actual_completion_date||'',
      bills: p.bills?.length ? p.bills.map(b=>{
        const o = {...emptyBill(),...b};
        BILL_NUM_KEYS.forEach(k=>{ o[k] = s(b[k]); });
        Object.keys(o).forEach(k=>{ if (o[k]==null) o[k]=''; });
        return o;
      }) : [emptyBill()],
      tocs:      p.tocs?.length     ? p.tocs.map(t=>({...emptyTOC(),...t}))    : [emptyTOC()],
      revisions: p.revisions?.length? p.revisions.map(r=>({...emptyRE(),...r})): [emptyRE()],
      letters:   p.letters?.length  ? p.letters.map(l=>({...emptyLetter(),...l})): [emptyLetter()],
      general_remarks: p.general_remarks||'',
      tender_id: p.tender_id!=null ? String(p.tender_id) : '',
      attachments: p.attachments||[],
    });
    setShowModal(true);
  }

  async function removeProject(p) {
    if(!window.confirm('Delete this project and all its data (bills, attachments)?'))return;
    const {error:de}=await supabase.from('civil_projects').delete().eq('id',p.id);
    if(de){toast.error(de.message);return;}
    removeFiles(p.attachments);
    load();
  }

  // ── Bill helpers ─────────────────────────────────────────────────
  const setBillField = (i,k,v) => setForm(f=>{
    const bills=[...f.bills]; bills[i]={...bills[i],[k]:v};
    if (k==='bill_amount'||k==='gst_amount') {
      const ba=num(bills[i].bill_amount), ga=num(bills[i].gst_amount);
      bills[i].total_amount = ba+ga > 0 ? String((ba+ga).toFixed(2)) : '';
    }
    return {...f,bills};
  });
  const addBill    = () => setForm(f=>({...f,bills:[...f.bills,emptyBill()]}));
  const removeBill = i  => setForm(f=>({...f,bills:f.bills.length>1?f.bills.filter((_,idx)=>idx!==i):[emptyBill()]}));

  const setTocField = (i,k,v)  => setForm(f=>{const tocs=[...f.tocs];tocs[i]={...tocs[i],[k]:v};return {...f,tocs};});
  const addToc      = ()        => setForm(f=>({...f,tocs:[...f.tocs,emptyTOC()]}));
  const removeToc   = i         => setForm(f=>({...f,tocs:f.tocs.filter((_,idx)=>idx!==i)}));

  const setRevField = (i,k,v)  => setForm(f=>{const revisions=[...f.revisions];revisions[i]={...revisions[i],[k]:v};return {...f,revisions};});
  const addRev      = ()        => setForm(f=>({...f,revisions:[...f.revisions,emptyRE()]}));
  const removeRev   = i         => setForm(f=>({...f,revisions:f.revisions.filter((_,idx)=>idx!==i)}));

  const setLetField = (i,k,v)  => setForm(f=>{const letters=[...f.letters];letters[i]={...letters[i],[k]:v};return {...f,letters};});
  const addLetter   = ()        => setForm(f=>({...f,letters:[...f.letters,emptyLetter()]}));
  const removeLetter= i         => setForm(f=>({...f,letters:f.letters.filter((_,idx)=>idx!==i)}));

  // ── Alerts: overdue SD / RA refunds, bills stuck at an office ────
  const alerts = useMemo(()=>{
    const out = [], today = localToday();
    projects.forEach(p=>{
      if (p.status==='Cancelled') return;
      if (has(p.security_deposit) && p.sd_return_status==='Pending' && p.sd_return_expected_date && p.sd_return_expected_date < today)
        out.push({ id:p.id, sev:'red', text:`${p.project_name} — Security Deposit return overdue by ${dayDiff(p.sd_return_expected_date)} days (₹${INR(p.security_deposit)})` });
      (p.bills||[]).forEach((b,i)=>{
        if (has(b.retention_amount) && b.ra_refund_expected && b.ra_refund_expected < today && b.ra_refund_status !== 'Refunded')
          out.push({ id:p.id, sev:'red', text:`${p.project_name} — Bill #${i+1} retention refund overdue by ${dayDiff(b.ra_refund_expected)} days (₹${INR(b.retention_amount)})` });
        const st = billStage(b);
        if (st && st.pending && st.days > STUCK_DAYS)
          out.push({ id:p.id, sev:'amber', text:`${p.project_name} — Bill #${i+1} waiting ${st.days} days (${st.text})` });
      });
    });
    return out;
  },[projects]);

  function jumpTo(id) {
    setSearch(''); setFStatus(''); setFFund(''); setFDue(false);
    setExpanded(e=>({...e,[id]:true}));
    setTimeout(()=>{ const el = document.getElementById('civil-proj-'+id); if (el) el.scrollIntoView({behavior:'smooth',block:'center'}); }, 80);
  }

  // ── Filtering ────────────────────────────────────────────────────
  const filtered = projects.filter(p=>
    (!search || p.project_name?.toLowerCase().includes(search.toLowerCase()) || p.location?.toLowerCase().includes(search.toLowerCase()))
    && (!fStatus || p.status===fStatus)
    && (!fFund   || p.fund===fFund)
    && (!fDue    || projTotals(p).out > 0)
  );

  // ── Summary stats (follow the filters) ───────────────────────────
  const sums = filtered.reduce((s,p)=>{ const t=projTotals(p); s.est+=num(p.estimated_amount); s.rec+=t.received; s.out+=t.out; return s; },{est:0,rec:0,out:0});

  function exportProjects() {
    const rows = filtered.map((p,i)=>{
      const t = projTotals(p);
      return { 'Sl':i+1, 'Project':p.project_name||'', 'Fund':p.fund||'', 'Location':p.location||'', 'Status':p.status||'',
        'Estimated Amount':num(p.estimated_amount), 'Est. Completion':p.estimated_completion_date||'',
        'Awarded Date':p.project_awarded_date||'', 'Start Date':p.actual_start_date||'', 'Completion Date':p.actual_completion_date||'',
        'EMD Status':p.emd_status||'', 'Security Deposit':num(p.security_deposit), 'SD Status':p.sd_return_status||'',
        'Bills':t.count, 'Billed Amount':t.billed, 'GST':t.gst, 'Bill Total':t.total, 'Amount Received':t.received,
        'TDS':t.tds, 'Retention':t.ret, 'Outstanding':t.out };
    });
    if (!rows.length) { toast.error('Nothing to export'); return; }
    const tot = k => rows.reduce((a,r)=>a+r[k],0);
    const totalRow = {}; Object.keys(rows[0]).forEach(k=>{ totalRow[k] = ''; });
    ['Estimated Amount','Security Deposit','Bills','Billed Amount','GST','Bill Total','Amount Received','TDS','Retention','Outstanding'].forEach(k=>{ totalRow[k]=tot(k); });
    totalRow['Sl']='TOTAL';
    rows.push(totalRow);

    const billRows = [];
    filtered.forEach(p=>(p.bills||[]).forEach((b,bi)=>{
      const st = billStage(b);
      billRows.push({ 'Project':p.project_name||'', 'Bill #':bi+1, 'Bill No':b.bill_no||'',
        'Bill Amount':num(b.bill_amount), 'GST':num(b.gst_amount), 'Total':billTotal(b),
        'Received':num(b.amount_received), 'Received Date':b.amount_received_date||'',
        'TDS':num(b.tds_deduction), 'Retention':num(b.retention_amount), 'Outstanding':billOutstanding(b),
        'RA Status':b.ra_refund_status||'', 'RA Expected':b.ra_refund_expected||'',
        'Stage':st?st.text+(st.pending?` (${st.days}d)`:''):'',
        'AE Generated':b.bill_generated_ae||'','AE Cleared':b.bill_cleared_ae||'',
        'AXE Generated':b.bill_generated_axe||'','AXE Cleared':b.bill_cleared_axe||'',
        'EE Generated':b.bill_generated_ee||'','EE Cleared':b.bill_cleared_ee||'',
        'FO Generated':b.bill_generated_fo||'','FO Cleared':b.bill_cleared_fo||'',
        'Remarks':b.remarks||'' });
    }));
    const sheets = [{ name:'Civil Projects', title:'Civil Construction — Projects', rows,
      widths:{ 'Sl':6, 'Project':34, 'Fund':14, 'Location':22, 'Status':12, 'Est. Completion':14, 'Awarded Date':14, 'Start Date':14, 'Completion Date':14 } }];
    if (billRows.length) sheets.push({ name:'Bills', title:'Civil Construction — Bill-wise', rows:billRows, widths:{ 'Project':34, 'Bill No':16, 'Stage':16, 'Remarks':30 } });
    saveStyledXlsx(`Civil_Projects_${localToday()}.xlsx`, sheets);
    toast.success('Exported ✅');
  }

  const statusColor = s => ({Active:'var(--green)',Completed:'var(--accent)','On Hold':'var(--amber)',Cancelled:'var(--red)'}[s]||'var(--text3)');
  const shownAlerts = allAlerts ? alerts : alerts.slice(0,6);

  return (
    <div>
      {/* Alerts */}
      {alerts.length>0&&(
        <div style={{border:'1px solid var(--red)',background:'var(--red)10',borderRadius:10,padding:'10px 14px',marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontWeight:700,fontSize:13,color:'var(--red)'}} onClick={()=>setAlertsOpen(o=>!o)}>
            <AlertTriangle size={15}/> {alerts.length} item{alerts.length>1?'s':''} need attention
            <span style={{marginLeft:'auto',fontSize:11,fontWeight:500}}>{alertsOpen?'Hide':'Show'}</span>
          </div>
          {alertsOpen&&(
            <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:4}}>
              {shownAlerts.map((a,i)=>(
                <div key={i} onClick={()=>jumpTo(a.id)} style={{fontSize:12,cursor:'pointer',color:a.sev==='red'?'var(--red)':'var(--amber)'}}>
                  {a.sev==='red'?'🔴':'🟠'} {a.text}
                </div>
              ))}
              {alerts.length>6&&<button className="btn btn-ghost btn-sm" style={{alignSelf:'flex-start'}} onClick={()=>setAllAlerts(v=>!v)}>{allAlerts?'Show fewer':`Show all ${alerts.length}`}</button>}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          {label:'Projects',              value:filtered.length,               color:'var(--accent)'},
          {label:'Active',                value:filtered.filter(p=>p.status==='Active').length, color:'var(--green)'},
          {label:'Total Estimated Value', value:`₹${INR(sums.est)}`,           color:'var(--text2)'},
          {label:'Total Amount Received', value:`₹${INR(sums.rec)}`,           color:'var(--green)'},
          {label:'Outstanding (click to filter)', value:`₹${INR(sums.out)}`,   color:'var(--red)', onClick:()=>setFDue(v=>!v)},
        ].map(s=>(
          <div key={s.label} className="stat-card" onClick={s.onClick} style={{cursor:s.onClick?'pointer':undefined,outline:s.onClick&&fDue?'2px solid var(--red)':undefined}}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{color:s.color,fontSize:18}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:220}}>
            <Search/><input placeholder="Search project name, location..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select style={{width:110,fontSize:12,padding:'4px 6px'}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="">All Status</option>
            {PROJ_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{width:120,fontSize:12,padding:'4px 6px'}} value={fFund} onChange={e=>setFFund(e.target.value)}>
            <option value="">All Funds</option>
            {FUND_TYPES.map(f=><option key={f} value={f}>{f}</option>)}
          </select>
          {(search||fStatus||fFund||fDue)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFStatus('');setFFund('');setFDue(false);}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} projects{fDue?' with dues':''}</span>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button className="btn btn-secondary" onClick={exportProjects}><Download size={14}/> Export Excel</button>
          {editable&&<button className="btn btn-primary" onClick={openNew}><Plus size={14}/> Add Project</button>}
        </div>
      </div>

      {/* Project Cards — Expandable */}
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {filtered.length===0&&(
          <div className="empty-state" style={{padding:40}}><Building2 size={32}/><h3>No projects found</h3></div>
        )}
        {filtered.map(p=>{
          const bills = p.bills||[];
          const t = projTotals(p);
          const isOpen = expanded[p.id];

          return (
            <div key={p.id} id={'civil-proj-'+p.id} style={{border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',background:'var(--surface)'}}>
              {/* Header */}
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',cursor:'pointer',
                background:isOpen?'var(--accent-light)':'var(--surface)'}}
                onClick={()=>setExpanded(e=>({...e,[p.id]:!e[p.id]}))}>
                <span style={{color:'var(--accent)'}}>{isOpen?<ChevronDown size={16}/>:<ChevronRight size={16}/>}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:15}}>{p.project_name}</div>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:3,display:'flex',gap:14,flexWrap:'wrap'}}>
                    {p.fund&&<span>🏦 {p.fund}</span>}
                    {p.location&&<span>📍 {p.location}</span>}
                    {has(p.estimated_amount)&&<span>💰 Est. ₹{INR(p.estimated_amount)}</span>}
                    {p.estimated_completion_date&&<span>🗓 {fmtDMY(p.estimated_completion_date)}</span>}
                  </div>
                </div>
                <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
                  {bills.length>0&&(
                    <div style={{textAlign:'right',fontSize:11}}>
                      <div style={{color:'var(--green)',fontWeight:600}}>Rcvd: ₹{INR(t.received)}</div>
                      {t.out>0&&<div style={{color:'var(--red)',fontWeight:600}}>Due: ₹{INR(t.out)}</div>}
                      <div style={{color:'var(--text3)'}}>{bills.length} bill{bills.length>1?'s':''}</div>
                    </div>
                  )}
                  <span style={{background:statusColor(p.status)+'22',color:statusColor(p.status),
                    borderRadius:6,padding:'3px 10px',fontSize:11,fontWeight:700,border:`1px solid ${statusColor(p.status)}44`}}>
                    {p.status}
                  </span>
                  <div style={{display:'flex',gap:4}} onClick={e=>e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setShowView(p)} title="View"><Eye size={13}/></button>
                    {editable&&<button className="btn btn-ghost btn-sm" title="Edit" onClick={()=>openEdit(p)}><Edit2 size={13}/></button>}
                    {editable&&<button className="btn btn-ghost btn-sm" title="Delete" style={{color:'var(--red)'}} onClick={()=>removeProject(p)}><Trash2 size={13}/></button>}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isOpen&&(
                <div style={{padding:'0 18px 18px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginTop:14}}>
                    <div style={{background:'var(--bg3)',borderRadius:10,padding:'12px 14px'}}>
                      <div style={{fontWeight:700,fontSize:12,color:'var(--amber)',marginBottom:8}}>💼 EMD</div>
                      <div style={{fontSize:11,display:'flex',flexDirection:'column',gap:4}}>
                        {has(p.tender_form_fee)&&<div><span style={{color:'var(--text3)'}}>Tender Fee:</span> ₹{INR(p.tender_form_fee)}</div>}
                        {has(p.emd_fee)&&<div><span style={{color:'var(--text3)'}}>EMD Fee:</span> ₹{INR(p.emd_fee)}</div>}
                        <div><span style={{color:'var(--text3)'}}>Status:</span> <span style={{fontWeight:600}}>{p.emd_status}</span></div>
                      </div>
                    </div>
                    <div style={{background:'var(--bg3)',borderRadius:10,padding:'12px 14px'}}>
                      <div style={{fontWeight:700,fontSize:12,color:'var(--purple)',marginBottom:8}}>🔒 Security Deposit</div>
                      <div style={{fontSize:11,display:'flex',flexDirection:'column',gap:4}}>
                        {has(p.security_deposit)&&<div><span style={{color:'var(--text3)'}}>Amount:</span> ₹{INR(p.security_deposit)}</div>}
                        {p.sd_return_expected_date&&<div><span style={{color:'var(--text3)'}}>Return Expected:</span> {fmtDMY(p.sd_return_expected_date)}</div>}
                        <div><span style={{color:'var(--text3)'}}>Status:</span> <span style={{fontWeight:600}}>{p.sd_return_status}</span></div>
                      </div>
                    </div>
                    <div style={{background:'var(--bg3)',borderRadius:10,padding:'12px 14px'}}>
                      <div style={{fontWeight:700,fontSize:12,color:'var(--cyan)',marginBottom:8}}>📅 Milestones</div>
                      <div style={{fontSize:11,display:'flex',flexDirection:'column',gap:3}}>
                        {p.project_awarded_date&&<div><span style={{color:'var(--text3)'}}>Awarded:</span> {fmtDMY(p.project_awarded_date)}</div>}
                        {p.site_handover_date&&<div><span style={{color:'var(--text3)'}}>Handover:</span> {fmtDMY(p.site_handover_date)}</div>}
                        {p.actual_start_date&&<div><span style={{color:'var(--text3)'}}>Started:</span> {fmtDMY(p.actual_start_date)}</div>}
                        {p.actual_completion_date&&<div><span style={{color:'var(--text3)'}}>Completed:</span> {fmtDMY(p.actual_completion_date)}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Bills Summary */}
                  {bills.length>0&&(
                    <div style={{marginTop:12}}>
                      <div style={{fontWeight:700,fontSize:12,color:'var(--green)',marginBottom:8}}>🧾 Bills Summary</div>
                      <div className="table-wrap" style={{maxHeight:260}}>
                        <table>
                          <thead>
                            <tr>
                              <th>Bill #</th><th>Bill No</th>
                              <th style={{textAlign:'right'}}>Bill Amt (₹)</th>
                              <th style={{textAlign:'right'}}>GST (₹)</th>
                              <th style={{textAlign:'right'}}>Total (₹)</th>
                              <th style={{textAlign:'right'}}>Received (₹)</th>
                              <th style={{textAlign:'right'}}>TDS (₹)</th>
                              <th style={{textAlign:'right'}}>Retention (₹)</th>
                              <th style={{textAlign:'right'}} title="Total − Received − TDS − Retention">Outstanding (₹)</th>
                              <th>RA Status</th>
                              <th>Stage</th>
                              <th>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bills.map((b,bi)=>{
                              const st = billStage(b), out = billOutstanding(b);
                              return (
                              <tr key={bi}>
                                <td style={{fontWeight:700,color:'var(--accent)'}}>#{bi+1}</td>
                                <td style={{fontSize:11}}>{b.bill_no||'—'}</td>
                                <td style={{textAlign:'right',fontSize:12}}>{has(b.bill_amount)?`₹${INR(b.bill_amount)}`:'—'}</td>
                                <td style={{textAlign:'right',fontSize:12,color:'var(--amber)'}}>{has(b.gst_amount)?`₹${INR(b.gst_amount)}`:'—'}</td>
                                <td style={{textAlign:'right',fontSize:12,fontWeight:600}}>{billTotal(b)>0?`₹${INR(billTotal(b))}`:'—'}</td>
                                <td style={{textAlign:'right',fontSize:12,color:'var(--green)',fontWeight:600}}>{has(b.amount_received)?`₹${INR(b.amount_received)}`:'—'}</td>
                                <td style={{textAlign:'right',fontSize:12,color:'var(--red)'}}>{has(b.tds_deduction)?`₹${INR(b.tds_deduction)}`:'—'}</td>
                                <td style={{textAlign:'right',fontSize:12,color:'var(--amber)'}}>{has(b.retention_amount)?`₹${INR(b.retention_amount)}`:'—'}</td>
                                <td style={{textAlign:'right',fontSize:12,fontWeight:700,color:out>0?'var(--red)':'var(--green)'}}>{billTotal(b)>0?`₹${INR(out)}`:'—'}</td>
                                <td style={{fontSize:11}}>{b.ra_refund_status||'—'}</td>
                                <td style={{fontSize:11,whiteSpace:'nowrap',color:st&&st.pending&&st.days>STUCK_DAYS?'var(--red)':undefined,fontWeight:st&&st.pending&&st.days>STUCK_DAYS?700:undefined}}>
                                  {st?`${st.text}${st.pending?` · ${st.days}d`:''}`:'—'}
                                </td>
                                <td style={{fontSize:11,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={b.remarks}>{b.remarks||'—'}</td>
                              </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{background:'var(--bg3)',fontWeight:700}}>
                              <td colSpan={2}>TOTAL</td>
                              <td style={{textAlign:'right'}}>₹{INR(t.billed)}</td>
                              <td style={{textAlign:'right',color:'var(--amber)'}}>₹{INR(t.gst)}</td>
                              <td style={{textAlign:'right'}}>₹{INR(t.total)}</td>
                              <td style={{textAlign:'right',color:'var(--green)'}}>₹{INR(t.received)}</td>
                              <td style={{textAlign:'right',color:'var(--red)'}}>₹{INR(t.tds)}</td>
                              <td style={{textAlign:'right',color:'var(--amber)'}}>₹{INR(t.ret)}</td>
                              <td style={{textAlign:'right',color:t.out>0?'var(--red)':'var(--green)'}}>₹{INR(t.out)}</td>
                              <td colSpan={3}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* TOC, Revisions, Letters */}
                  {((p.tocs||[]).some(x=>x.requested_date)||(p.revisions||[]).some(r=>r.generated_date)||(p.letters||[]).some(l=>l.submission_date))&&(
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:12}}>
                      {(p.tocs||[]).some(x=>x.requested_date)&&(
                        <div style={{background:'#fef3c7',borderRadius:8,padding:'10px 12px'}}>
                          <div style={{fontWeight:700,fontSize:12,color:'#92400e',marginBottom:6}}>⏱ TOC Extensions</div>
                          {(p.tocs||[]).map((x,i)=>x.requested_date?(
                            <div key={i} style={{fontSize:11,marginBottom:4,color:'#451a03'}}>
                              <span style={{fontWeight:600}}>TOC #{i+1}:</span> {fmtDMY(x.requested_date)} → {fmtDMY(x.approved_date)||'Pending'} {x.validity_period&&`(${x.validity_period})`}
                            </div>
                          ):null)}
                        </div>
                      )}
                      {(p.revisions||[]).some(r=>r.generated_date)&&(
                        <div style={{background:'#eff6ff',borderRadius:8,padding:'10px 12px'}}>
                          <div style={{fontWeight:700,fontSize:12,color:'#1d4ed8',marginBottom:6}}>📋 Revisions (RE)</div>
                          {(p.revisions||[]).map((r,i)=>r.generated_date?(
                            <div key={i} style={{fontSize:11,marginBottom:4,color:'#172554'}}>
                              <span style={{fontWeight:600}}>RE #{i+1}:</span> {fmtDMY(r.generated_date)} → {fmtDMY(r.approved_date)||'Pending'}
                            </div>
                          ):null)}
                        </div>
                      )}
                      {(p.letters||[]).some(l=>l.submission_date)&&(
                        <div style={{background:'#f0fdf4',borderRadius:8,padding:'10px 12px'}}>
                          <div style={{fontWeight:700,fontSize:12,color:'#15803d',marginBottom:6}}>📨 Letter Submissions</div>
                          {(p.letters||[]).map((l,i)=>l.submission_date?(
                            <div key={i} style={{fontSize:11,marginBottom:4,color:'#052e16'}}>
                              <span style={{fontWeight:600}}>#{i+1}:</span> {fmtDMY(l.submission_date)} — {l.reason||'—'}
                            </div>
                          ):null)}
                        </div>
                      )}
                    </div>
                  )}
                  {(p.attachments||[]).length>0&&(
                    <div style={{marginTop:10,fontSize:12}}>
                      <Paperclip size={12}/> {(p.attachments||[]).map((a,i)=>(
                        <a key={i} href="#open" onClick={e=>{e.preventDefault();openAttachment(a);}} style={{color:'var(--accent)',marginRight:12}}>{a.name}</a>
                      ))}
                    </div>
                  )}
                  {p.general_remarks&&(
                    <div style={{marginTop:10,padding:'8px 12px',background:'var(--bg3)',borderRadius:8,fontSize:12,fontStyle:'italic',color:'var(--text2)'}}>
                      💬 {p.general_remarks}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showView&&<ProjectView p={showView} editable={editable} onClose={()=>setShowView(null)} onEdit={openEdit}/>}

      {/* ═══ ADD / EDIT MODAL ═══ */}
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal modal-lg" style={{maxWidth:860,maxHeight:'94vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Project</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">

              <SH title="A — Project Details" icon={Building2} mt={0}/>
              <div className="form-grid">
                <FG label="Project Name *" full>
                  <input value={form.project_name} onChange={e=>sf('project_name',e.target.value)} placeholder="e.g. Kummil Site Project"/>
                </FG>
                <FG label="Fund / Client">
                  <select value={form.fund} onChange={e=>sf('fund',e.target.value)}>
                    <option value="">Select Fund</option>
                    {FUND_TYPES.map(f=><option key={f} value={f}>{f}</option>)}
                  </select>
                </FG>
                <FG label="Location">
                  <input value={form.location} onChange={e=>sf('location',e.target.value)} placeholder="Project location"/>
                </FG>
                <FG label="Estimated Amount (₹)">
                  <input type="number" min="0" value={form.estimated_amount} onChange={e=>sf('estimated_amount',e.target.value)} placeholder="0.00"/>
                </FG>
                <FG label="Estimated Completion Date">
                  <DateInput value={form.estimated_completion_date} onChange={e=>sf('estimated_completion_date',e.target.value)}/>
                </FG>
                <FG label="Status">
                  <select value={form.status} onChange={e=>sf('status',e.target.value)}>
                    {PROJ_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </FG>
              </div>

              <SH title="B — EMD (Earnest Money Deposit)" color="#d97706" icon={DollarSign}/>
              <div className="form-grid">
                <FG label="Tender Form Fee (₹)"><input type="number" min="0" value={form.tender_form_fee} onChange={e=>sf('tender_form_fee',e.target.value)} placeholder="0.00"/></FG>
                <FG label="EMD Fee (₹)"><input type="number" min="0" value={form.emd_fee} onChange={e=>sf('emd_fee',e.target.value)} placeholder="0.00"/></FG>
                <FG label="EMD Status">
                  <select value={form.emd_status} onChange={e=>sf('emd_status',e.target.value)}>
                    {EMD_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </FG>
              </div>

              <SH title="C — Security Deposit (SD)" color="#7c3aed"/>
              <div className="form-grid">
                <FG label="Security Deposit (₹)"><input type="number" min="0" value={form.security_deposit} onChange={e=>sf('security_deposit',e.target.value)} placeholder="0.00"/></FG>
                <FG label="SD Return Expected Date"><DateInput value={form.sd_return_expected_date} onChange={e=>sf('sd_return_expected_date',e.target.value)}/></FG>
                <FG label="SD Return Status">
                  <select value={form.sd_return_status} onChange={e=>sf('sd_return_status',e.target.value)}>
                    {SD_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </FG>
              </div>

              <SH title="D — Milestone Dates" color="#0891b2" icon={Calendar}/>
              <div className="form-grid">
                <FG label="Project Awarded Date"><DateInput value={form.project_awarded_date} onChange={e=>sf('project_awarded_date',e.target.value)}/></FG>
                <FG label="Site Handover Date"><DateInput value={form.site_handover_date} onChange={e=>sf('site_handover_date',e.target.value)}/></FG>
                <FG label="Actual Project Started Date"><DateInput value={form.actual_start_date} onChange={e=>sf('actual_start_date',e.target.value)}/></FG>
                <FG label="Actual Project Completion Date"><DateInput value={form.actual_completion_date} onChange={e=>sf('actual_completion_date',e.target.value)}/></FG>
              </div>

              <div style={{marginTop:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <SH title="E — Bills" color="#16a34a" icon={FileText} mt={0}/>
                <button className="btn btn-secondary btn-sm" onClick={addBill} style={{flexShrink:0}}><Plus size={13}/> Add Bill</button>
              </div>
              {form.bills.map((b,bi)=>(
                <div key={bi} style={{border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',marginBottom:12,background:'var(--bg3)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <span style={{fontWeight:700,color:'var(--green)',fontSize:13}}>Bill #{bi+1}
                      {billTotal(b)>0&&<span style={{marginLeft:12,fontWeight:600,fontSize:12,color:billOutstanding(b)>0?'var(--red)':'var(--green)'}}>Outstanding ₹{INR(billOutstanding(b))}</span>}
                    </span>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>removeBill(bi)}><Trash2 size={13}/></button>
                  </div>
                  <div className="form-grid">
                    <FG label="Bill Number / Reference">
                      <input value={b.bill_no} onChange={e=>setBillField(bi,'bill_no',e.target.value)} placeholder="Bill reference number"/>
                    </FG>
                    <FG label="Bill Amount (₹)">
                      <input type="number" min="0" value={b.bill_amount} onChange={e=>setBillField(bi,'bill_amount',e.target.value)} placeholder="0.00"/>
                    </FG>
                    <FG label="GST Amount (₹)">
                      <input type="number" min="0" value={b.gst_amount} onChange={e=>setBillField(bi,'gst_amount',e.target.value)} placeholder="0.00"/>
                    </FG>
                    <FG label="Total Amount (₹) — auto">
                      <input value={b.total_amount} readOnly style={{background:'var(--accent-light)',fontWeight:700,color:'var(--green)',cursor:'not-allowed'}}/>
                    </FG>
                  </div>
                  <div style={{background:'var(--surface)',borderRadius:8,padding:'10px 12px',marginTop:8}}>
                    <div style={{fontSize:11,fontWeight:600,color:'var(--text3)',marginBottom:8}}>📋 Office Processing Dates</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
                      {OFFICES.map(([label,key])=>(
                        <div key={key} style={{background:'var(--bg3)',borderRadius:6,padding:'8px 10px'}}>
                          <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',marginBottom:6}}>{label}</div>
                          <div style={{fontSize:10,marginBottom:3}}>Generated:</div>
                          <DateInput value={b[`bill_generated_${key}`]} onChange={e=>setBillField(bi,`bill_generated_${key}`,e.target.value)} style={{fontSize:10,padding:'2px 4px',marginBottom:6}}/>
                          <div style={{fontSize:10,marginBottom:3}}>Cleared:</div>
                          <DateInput value={b[`bill_cleared_${key}`]} onChange={e=>setBillField(bi,`bill_cleared_${key}`,e.target.value)} style={{fontSize:10,padding:'2px 4px'}}/>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-grid" style={{marginTop:10}}>
                    <FG label="Amount Received (₹)">
                      <input type="number" min="0" value={b.amount_received} onChange={e=>setBillField(bi,'amount_received',e.target.value)} placeholder="0.00" style={{color:'var(--green)',fontWeight:600}}/>
                    </FG>
                    <FG label="Amount Received Date">
                      <DateInput value={b.amount_received_date} onChange={e=>setBillField(bi,'amount_received_date',e.target.value)}/>
                    </FG>
                    <FG label="TDS Deduction (₹)">
                      <input type="number" min="0" value={b.tds_deduction} onChange={e=>setBillField(bi,'tds_deduction',e.target.value)} placeholder="0.00"/>
                    </FG>
                    <FG label="Retention Amount / RA (₹)">
                      <input type="number" min="0" value={b.retention_amount} onChange={e=>setBillField(bi,'retention_amount',e.target.value)} placeholder="0.00"/>
                    </FG>
                    <FG label="RA Refund Expected Date">
                      <DateInput value={b.ra_refund_expected} onChange={e=>setBillField(bi,'ra_refund_expected',e.target.value)}/>
                    </FG>
                    <FG label="RA Refund Status">
                      <select value={b.ra_refund_status} onChange={e=>setBillField(bi,'ra_refund_status',e.target.value)}>
                        <option value="">Select</option>
                        {RA_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </FG>
                    <FG label="Remarks" full>
                      <input value={b.remarks} onChange={e=>setBillField(bi,'remarks',e.target.value)} placeholder="Bill remarks"/>
                    </FG>
                  </div>
                </div>
              ))}

              <div style={{marginTop:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <SH title="F — TOC Extensions (Time of Completion)" color="#d97706" mt={0}/>
                <button className="btn btn-secondary btn-sm" onClick={addToc}><Plus size={13}/> Add TOC</button>
              </div>
              {form.tocs.map((t,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 2fr auto',gap:8,marginBottom:8,alignItems:'end',background:'var(--bg3)',padding:'10px 12px',borderRadius:8}}>
                  <FG label={`TOC #${i+1} — Requested Date`} style={{margin:0}}><DateInput value={t.requested_date} onChange={e=>setTocField(i,'requested_date',e.target.value)}/></FG>
                  <FG label="Approved Date" style={{margin:0}}><DateInput value={t.approved_date} onChange={e=>setTocField(i,'approved_date',e.target.value)}/></FG>
                  <FG label="Validity Period" style={{margin:0}}><input value={t.validity_period} onChange={e=>setTocField(i,'validity_period',e.target.value)} placeholder="e.g. 6 months"/></FG>
                  <FG label="Remarks" style={{margin:0}}><input value={t.remarks} onChange={e=>setTocField(i,'remarks',e.target.value)} placeholder="Remarks"/></FG>
                  {form.tocs.length>1&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)',alignSelf:'flex-end',marginBottom:2}} onClick={()=>removeToc(i)}><Trash2 size={13}/></button>}
                </div>
              ))}

              <div style={{marginTop:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <SH title="G — Revisions (RE)" color="#0891b2" mt={0}/>
                <button className="btn btn-secondary btn-sm" onClick={addRev}><Plus size={13}/> Add RE</button>
              </div>
              {form.revisions.map((r,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 1fr 2fr auto',gap:8,marginBottom:8,alignItems:'end',background:'var(--bg3)',padding:'10px 12px',borderRadius:8}}>
                  <FG label={`RE #${i+1} — Generated Date`} style={{margin:0}}><DateInput value={r.generated_date} onChange={e=>setRevField(i,'generated_date',e.target.value)}/></FG>
                  <FG label="Approved Date" style={{margin:0}}><DateInput value={r.approved_date} onChange={e=>setRevField(i,'approved_date',e.target.value)}/></FG>
                  <FG label="Remarks" style={{margin:0}}><input value={r.remarks} onChange={e=>setRevField(i,'remarks',e.target.value)} placeholder="Remarks"/></FG>
                  {form.revisions.length>1&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)',alignSelf:'flex-end',marginBottom:2}} onClick={()=>removeRev(i)}><Trash2 size={13}/></button>}
                </div>
              ))}

              <div style={{marginTop:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <SH title="H — Letter Submissions" color="#dc2626" mt={0}/>
                <button className="btn btn-secondary btn-sm" onClick={addLetter}><Plus size={13}/> Add Letter</button>
              </div>
              {form.letters.map((l,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 2fr 2fr auto',gap:8,marginBottom:8,alignItems:'end',background:'var(--bg3)',padding:'10px 12px',borderRadius:8}}>
                  <FG label={`Letter #${i+1} — Submission Date`} style={{margin:0}}><DateInput value={l.submission_date} onChange={e=>setLetField(i,'submission_date',e.target.value)}/></FG>
                  <FG label="Reason" style={{margin:0}}><input value={l.reason} onChange={e=>setLetField(i,'reason',e.target.value)} placeholder="Reason for letter"/></FG>
                  <FG label="Remarks" style={{margin:0}}><input value={l.remarks} onChange={e=>setLetField(i,'remarks',e.target.value)} placeholder="Remarks"/></FG>
                  {form.letters.length>1&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)',alignSelf:'flex-end',marginBottom:2}} onClick={()=>removeLetter(i)}><Trash2 size={13}/></button>}
                </div>
              ))}

              <SH title="I — Attachments (work order, agreement, bill copies…)" color="#475569" icon={Paperclip}/>
              <Attachments value={form.attachments} onChange={v=>sf('attachments',v)} folder={`projects/${uploadKey}`} editable/>

              <div className="form-grid" style={{marginTop:14}}>
                <FG label="General Remarks" full>
                  <textarea rows={2} value={form.general_remarks} onChange={e=>sf('general_remarks',e.target.value)} placeholder="Any general notes about this project..."/>
                </FG>
              </div>

            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update Project':'Save Project'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TENDER VIEW (read-only popup)
// ═══════════════════════════════════════════════════════════════════
function TenderView({ r, onClose, onEdit, editable, canConvert, onConvert }) {
  const money = v => has(v) ? '₹' + INR(v) : '—';
  const feeForm = r.tender_form_fee ?? r.tender_fee, feeEmd = r.emd_fee ?? r.emd;
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:760,maxHeight:'94vh',overflowY:'auto'}}>
        <div className="modal-header">
          <span className="modal-title">📄 Tender {r.tender_ref||''}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <KV label="Work Description"><span style={{whiteSpace:'pre-wrap'}}>{r.work_description||'—'}</span></KV>
          <KV label="Organization">{r.organization||'—'}</KV>
          <KV label="District">{r.district||'—'}</KV>
          <KV label="Project Location">{r.project_location||'—'}</KV>
          <KV label="Nature of Fund">{r.nature_of_fund||'—'}</KV>
          <KV label="Tender Value">{money(r.tender_value)}</KV>
          <KV label="Published">{fmtDMY(r.tender_published_date)||'—'}</KV>
          <KV label="Closing">{fmtDMY(r.tender_closing_date)||'—'}</KV>
          <KV label="Bid Opening">{fmtDMY(r.bid_opening_date)||'—'}</KV>
          <KV label="Work Completion">{r.work_completion||'—'}</KV>
          <KV label="Participation">{r.participation||'—'}</KV>
          <KV label="Submitted On">{fmtDMY(r.tender_submitted_date)||'—'}</KV>
          <KV label="Tender Status">{r.tender_status||'—'}</KV>
          <KV label="Tender Form Fee">{money(feeForm)}</KV>
          <KV label="EMD Fee">{money(feeEmd)}</KV>
          <KV label="Security Amount">{money(r.security_amount)}</KV>
          <KV label="Remarks"><span style={{whiteSpace:'pre-wrap'}}>{r.remarks||'—'}</span></KV>
          <div style={{marginTop:12}}>
            <div style={{fontWeight:600,fontSize:12,color:'var(--text2)',marginBottom:4}}>Attachments</div>
            <Attachments value={r.attachments} editable={false} folder=""/>
          </div>
          <div style={{marginTop:14,fontSize:11,color:'var(--text3)'}}>
            Created {fmtStamp(r.created_at)}{r.created_by?` by ${r.created_by}`:''} · Last updated {fmtStamp(r.updated_at)}{r.updated_by?` by ${r.updated_by}`:''}
          </div>
        </div>
        <div className="modal-footer">
          {canConvert&&(r.tender_status==='Awarded'||r.participation==='Awarded')&&<button className="btn btn-secondary" onClick={()=>{onClose();onConvert(r);}}><ArrowRight size={13}/> Create Project</button>}
          {editable&&<button className="btn btn-secondary" onClick={()=>{onClose();onEdit(r);}}>Edit</button>}
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TENDER DB
// ═══════════════════════════════════════════════════════════════════
const isOpenish = r => !['Closed','Awarded','Cancelled','Rejected'].includes(r.tender_status) && r.participation !== 'Rejected';
const closingSoon = r => {
  if (!r.tender_closing_date || !isOpenish(r)) return false;
  const left = -dayDiff(r.tender_closing_date);
  return left >= 0 && left <= SOON_DAYS;
};
const tenderKey = r => {
  const ref = String(r.tender_ref||'').trim().toLowerCase();
  if (ref) return 'ref:' + ref;
  return 'sig:' + [r.work_description, r.tender_published_date, r.tender_value].map(x => String(x||'').trim().toLowerCase()).join('|');
};

function TenderDB({ editable, canConvert, onConvert }) {
  const [records,    setRecords]    = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [showView,   setShowView]   = useState(null);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [search,     setSearch]     = useState('');
  const [fOrg,       setFOrg]       = useState('');
  const [fDistrict,  setFDistrict]  = useState('');
  const [fFund,      setFFund]      = useState('');
  const [fParticip,  setFParticip]  = useState('');
  const [fStatus,    setFStatus]    = useState('');
  const [fYear,      setFYear]      = useState('');
  const [fSoon,      setFSoon]      = useState(false);
  const [sort,       setSort]       = useState({ key:'', dir:'asc' });
  const [page,       setPage]       = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [uploadKey,  setUploadKey]  = useState('');
  const [form, setForm] = useState(emptyTender);
  const fileRef = React.useRef();
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{ load(); },[]);
  useLiveRefresh(['civil_tenders'], () => load(), true, 2500);
  useEffect(()=>{ setPage(0); },[search,fOrg,fDistrict,fFund,fParticip,fStatus,fYear,fSoon,sort]);
  async function load() {
    try { setRecords(await fetchAllPaged(() => supabase.from('civil_tenders').select('*').order('tender_published_date',{ascending:false}).order('id'))); }
    catch (e) { toast.error('Could not load tenders: ' + e.message); }
  }

  async function save() {
    if (!form.work_description.trim()) { toast.error('Work Description is required'); return; }
    if (form.tender_published_date && form.tender_closing_date && form.tender_closing_date < form.tender_published_date) { toast.error('Closing date is before the published date'); return; }
    if (['tender_value','tender_form_fee','emd_fee','security_amount'].some(k=>num(form[k])<0)) { toast.error('Amounts cannot be negative'); return; }
    const ref = (form.tender_ref||'').trim();
    if (ref && records.some(r => r.id !== editing && (r.tender_ref||'').trim().toLowerCase() === ref.toLowerCase()) && !window.confirm(`Tender reference "${ref}" already exists. Save anyway?`)) return;
    setSaving(true);
    const n = v => v!==''&&v!=null ? Number(String(v).replace(/[^0-9.]/g,'')) : null;
    const d = v => v&&String(v).trim() ? v : null;
    const feeForm = n(form.tender_form_fee), feeEmd = n(form.emd_fee);
    const core = {
      tender_ref: ref,
      organization: form.organization, district: form.district,
      project_location: form.project_location, work_description: form.work_description.trim(),
      nature_of_fund: form.nature_of_fund,
      tender_value: n(form.tender_value),
      tender_published_date: d(form.tender_published_date),
      tender_closing_date:   d(form.tender_closing_date),
      bid_opening_date:      d(form.bid_opening_date),
      work_completion: form.work_completion,
      participation: form.participation,
      tender_submitted_date: form.participation === 'NO' ? null : d(form.tender_submitted_date),
      tender_status: form.tender_status,
      // keep the old duplicate columns in step so nothing reads a stale value
      tender_form_fee: feeForm, tender_fee: feeForm,
      emd_fee: feeEmd, emd: feeEmd,
      security_amount: n(form.security_amount),
      remarks: form.remarks,
    };
    const me = await whoAmI();
    const extra = { attachments: form.attachments||[], updated_by: me, updated_at: new Date().toISOString(),
                    ...(editing ? {} : { created_by: me }) };
    const { error } = await saveRow('civil_tenders', editing, core, extra);
    if (error) toast.error(error.message);
    else { toast.success(editing?'Updated ✅':'Saved ✅'); setShowModal(false); load(); }
    setSaving(false);
  }

  function openNew() { setEditing(null); setUploadKey('new_'+Date.now()); setForm(emptyTender()); setShowModal(true); }
  function openEdit(r) {
    setEditing(r.id);
    setUploadKey(String(r.id));
    const s = v => v != null ? String(v) : '';
    const e = emptyTender();
    setForm({ ...e,
      tender_ref: r.tender_ref||'', organization: r.organization||'', district: r.district||'',
      project_location: r.project_location||'', work_description: r.work_description||'',
      nature_of_fund: r.nature_of_fund||'', tender_value: s(r.tender_value),
      tender_published_date: r.tender_published_date||'', tender_closing_date: r.tender_closing_date||'',
      bid_opening_date: r.bid_opening_date||'', work_completion: r.work_completion||'',
      participation: r.participation||'NO', tender_submitted_date: r.tender_submitted_date||'',
      tender_status: r.tender_status||'',
      tender_form_fee: s(r.tender_form_fee ?? r.tender_fee),
      emd_fee: s(r.emd_fee ?? r.emd),
      security_amount: s(r.security_amount), remarks: r.remarks||'',
      attachments: r.attachments||[],
    });
    setShowModal(true);
  }

  async function removeTender(r) {
    if(!window.confirm('Delete this tender?'))return;
    const {error:de}=await supabase.from('civil_tenders').delete().eq('id',r.id);
    if(de){toast.error(de.message);return;}
    removeFiles(r.attachments);
    load();
  }

  async function convert(r) {
    const { data } = await supabase.from('civil_projects').select('id,project_name').eq('tender_id', String(r.id)).limit(1);
    if (data && data.length) { toast.error(`Already converted to project: ${data[0].project_name}`); return; }
    if (!window.confirm('Create a project from this tender?\nThe tender will be marked Awarded once the project is saved.')) return;
    onConvert(r);
  }

  // ── Import from Excel ─────────────────────────────────────────
  async function handleImport(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, {type:'array', cellDates:true});
      const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {header:1, defval:''});
      if (raw.length < 2) { toast.error('File is empty'); return; }
      const norm = s => String(s||'').toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
      const headers = raw[0].map(norm);
      // exact header name wins; only then fall back to "contains"
      const pick = (names, inc) => {
        for (const nm of names) { const i = headers.indexOf(nm); if (i >= 0) return i; }
        if (inc) { const i = headers.findIndex(h => h.includes(inc)); if (i >= 0) return i; }
        return -1;
      };
      const C = {
        ref: pick(['tender ref','tender reference','tender reference number'],'tender ref'),
        feeA: pick(['tender fee'],null), feeB: pick(['tender form fee'],'form fee'),
        emdA: pick(['emd'],null), emdB: pick(['emd fee'],'emd fee'),
        org: pick(['organization','organisation'],'organi'), dist: pick(['district'],'district'),
        loc: pick(['project location','location'],'location'),
        desc: pick(['work description'],'description'), fund: pick(['nature of fund'],'fund'),
        val: pick(['tender value'],'tender value'),
        pub: pick(['tender published date','published date'],'publish'),
        clo: pick(['tender closing date','closing date'],'clos'),
        opn: pick(['bid opening date','opening date'],'opening'),
        sub: pick(['tender submitted date','submitted date'],'submitted'),
        comp: pick(['work completion','work completion period'],'completion'),
        part: pick(['tender participation by armstech','tender participation','participation'],'participation'),
        stat: pick(['tender status','status'],'status'),
        sec: pick(['security amount','security'],'security'),
        rem: pick(['remarks'],'remark'),
      };
      const g = (row, k) => (C[k] >= 0 ? row[C[k]] : '');
      const parseNum = v => { if (!v) return null; const x = Number(String(v).replace(/[^0-9.]/g,'')); return isNaN(x) || x === 0 ? null : x; };
      const normPart = v => { const t = String(v||'').trim(); if (!t) return 'NO'; if (/^y(es)?$/i.test(t)) return 'YES'; if (/^no?$/i.test(t)) return 'NO'; return canon(t, PARTICIP_OPTS); };
      const me = await whoAmI();
      const rows = raw.slice(1).filter(r=>r.some(v=>v!==''&&v!==null));
      const parsed = rows.map(row=>{
        const feeForm = parseNum(g(row,'feeB')) ?? parseNum(g(row,'feeA'));
        const feeEmd  = parseNum(g(row,'emdB'))  ?? parseNum(g(row,'emdA'));
        const part = normPart(g(row,'part'));
        return {
          tender_ref:            String(g(row,'ref')||'').trim(),
          tender_form_fee: feeForm, tender_fee: feeForm,
          emd_fee: feeEmd, emd: feeEmd,
          organization:          canon(g(row,'org'), ORGANIZATIONS),
          district:              String(g(row,'dist')||'').trim().toUpperCase(),
          project_location:      String(g(row,'loc')||'').trim(),
          work_description:      String(g(row,'desc')||'').trim(),
          nature_of_fund:        canon(g(row,'fund'), FUND_TYPES),
          tender_value:          parseNum(g(row,'val')),
          tender_published_date: parseXlDate(g(row,'pub')),
          tender_closing_date:   parseXlDate(g(row,'clo')),
          bid_opening_date:      parseXlDate(g(row,'opn')),
          tender_submitted_date: part === 'NO' ? null : parseXlDate(g(row,'sub')),
          work_completion:       String(g(row,'comp')||'').trim(),
          participation:         part,
          tender_status:         canon(g(row,'stat'), STATUS_OPTS),
          security_amount:       parseNum(g(row,'sec')),
          remarks:               String(g(row,'rem')||'').trim(),
        };
      }).filter(r=>r.work_description||r.tender_ref);
      if (!parsed.length) { toast.error('No valid rows found'); return; }
      // skip tenders already in the DB (or repeated inside the file) — works for blank references too
      const have = new Set(records.map(tenderKey));
      const fresh = parsed.filter(r => { const k = tenderKey(r); if (have.has(k)) return false; have.add(k); return true; });
      const skipped = parsed.length - fresh.length;
      if (!fresh.length) { toast.error(`All ${parsed.length} rows already exist — nothing imported`); return; }
      let done = 0, failMsg = '';
      for (let i = 0; i < fresh.length; i += 200) {
        const chunk = fresh.slice(i, i + 200);
        let { error } = await supabase.from('civil_tenders').insert(chunk.map(r => ({ ...r, created_by: me, updated_by: me })));
        if (error && isMissingCol(error)) ({ error } = await supabase.from('civil_tenders').insert(chunk));
        if (error) { failMsg = error.message; break; }
        done += chunk.length;
      }
      if (failMsg) toast.error(`Imported ${done} of ${fresh.length}. Stopped: ${failMsg}`);
      else toast.success(`✅ ${done} tenders imported${skipped?` (${skipped} duplicate${skipped>1?'s':''} skipped)`:''}`);
      load();
      setShowImport(false);
    } catch (err) {
      toast.error('Import failed: ' + (err.message || err));
    } finally {
      setImporting(false);
    }
  }

  // ── Unique values for dynamic filters ─────────────────────────
  const uniqueOrgs  = [...new Set(records.map(r=>r.organization).filter(Boolean))].sort();
  const uniqueDists = [...new Set(records.map(r=>r.district).filter(Boolean))].sort();
  const uniqueFunds = [...new Set(records.map(r=>r.nature_of_fund).filter(Boolean))].sort();

  // ── Filter + sort + page ──────────────────────────────────────
  const filtered = records.filter(r=>{
    const q = search.toLowerCase();
    return (!q || r.work_description?.toLowerCase().includes(q)
                || r.tender_ref?.toLowerCase().includes(q)
                || r.project_location?.toLowerCase().includes(q)
                || r.organization?.toLowerCase().includes(q))
      && (!fOrg      || r.organization    === fOrg)
      && (!fDistrict || r.district        === fDistrict)
      && (!fFund     || r.nature_of_fund  === fFund)
      && (!fParticip || r.participation   === fParticip)
      && (!fStatus   || r.tender_status   === fStatus)
      && (!fYear     || r.tender_published_date?.startsWith(String(fYear)))
      && (!fSoon     || closingSoon(r));
  });
  const NUMERIC_SORT = ['tender_value'];
  const sorted = !sort.key ? filtered : [...filtered].sort((a,b)=>{
    const x = a[sort.key], y = b[sort.key], dir = sort.dir === 'asc' ? 1 : -1;
    const ex = x == null || x === '', ey = y == null || y === '';
    if (ex && ey) return 0; if (ex) return 1; if (ey) return -1;   // blanks always last
    if (NUMERIC_SORT.includes(sort.key)) return (Number(x) - Number(y)) * dir;
    return String(x).localeCompare(String(y), undefined, { numeric:true, sensitivity:'base' }) * dir;
  });
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const cur   = Math.min(page, pages - 1);
  const shown = sorted.slice(cur * PAGE_SIZE, (cur + 1) * PAGE_SIZE);
  const toggleSort = key => setSort(s => s.key !== key ? { key, dir:'asc' } : s.dir === 'asc' ? { key, dir:'desc' } : { key:'', dir:'asc' });
  const th = (key, label, style) => (
    <th onClick={()=>toggleSort(key)} style={{cursor:'pointer',whiteSpace:'nowrap',userSelect:'none',...style}} title="Click to sort">
      {label}{sort.key===key&&(sort.dir==='asc'?<ArrowUp size={11} style={{marginLeft:3}}/>:<ArrowDown size={11} style={{marginLeft:3}}/>)}
    </th>
  );

  function exportTenders() {
    const rows = sorted.map((r,i)=>({
      'Sl':i+1, 'Tender Ref':r.tender_ref||'', 'Organization':r.organization||'', 'District':r.district||'',
      'Location':r.project_location||'', 'Work Description':r.work_description||'', 'Fund':r.nature_of_fund||'',
      'Tender Value':num(r.tender_value), 'Published':r.tender_published_date||'', 'Closing':r.tender_closing_date||'',
      'Bid Opening':r.bid_opening_date||'', 'Submitted':r.tender_submitted_date||'', 'Completion':r.work_completion||'', 'Participation':r.participation||'',
      'Status':r.tender_status||'', 'Form Fee':num(r.tender_form_fee ?? r.tender_fee), 'EMD Fee':num(r.emd_fee ?? r.emd),
      'Security':num(r.security_amount), 'Remarks':r.remarks||'',
    }));
    if (!rows.length) { toast.error('Nothing to export'); return; }
    const tot = k => rows.reduce((t,r)=>t+r[k],0);
    const totalRow = {}; Object.keys(rows[0]).forEach(k=>{ totalRow[k] = ''; });
    ['Tender Value','Form Fee','EMD Fee','Security'].forEach(k=>{ totalRow[k] = tot(k); });
    totalRow['Sl'] = 'TOTAL';
    rows.push(totalRow);
    saveStyledXlsx(`Civil_Tenders_${localToday()}.xlsx`, [{ name:'Tender DB', title:'Civil Construction — Tender DB', rows,
      widths:{ 'Sl':6, 'Tender Ref':26, 'Organization':14, 'District':18, 'Location':22, 'Work Description':48, 'Fund':14, 'Published':12, 'Closing':12, 'Bid Opening':12, 'Submitted':12, 'Completion':14, 'Participation':13, 'Status':13, 'Remarks':30 } }]);
    toast.success('Exported ✅');
  }

  // ── Summary ───────────────────────────────────────────────────
  const totalValue    = filtered.reduce((s,r)=>s+num(r.tender_value),0);
  const awarded       = filtered.filter(r=>r.tender_status==='Awarded'||r.participation==='Awarded').length;
  const participated  = filtered.filter(r=>r.participation==='YES'||r.participation==='Applied'||r.participation==='Shortlisted'||r.participation==='Awarded').length;
  const soonCount     = records.filter(closingSoon).length;

  const participBadge = v => {
    const map = {YES:'badge-green',Awarded:'badge-green',NO:'badge-red',
                  Applied:'badge-blue',Shortlisted:'badge-cyan',Rejected:'badge-red'};
    return <span className={`badge ${map[v]||'badge-gray'}`} style={{fontSize:9}}>{v||'—'}</span>;
  };
  const statusBadge = v => {
    const map = {Open:'badge-green',Awarded:'badge-green',Closed:'badge-gray',
                  Cancelled:'badge-red',Rejected:'badge-red','Under Review':'badge-amber',Pending:'badge-amber'};
    return v ? <span className={`badge ${map[v]||'badge-gray'}`} style={{fontSize:9}}>{v}</span> : <span style={{color:'var(--text3)'}}>—</span>;
  };
  const today = localToday();
  const money = v => has(v) ? `₹${INR(v)}` : '—';
  const COLS = 20;

  return (
    <div>
      {/* Summary */}
      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          {label:'Total Tenders',     value:filtered.length,           color:'var(--accent)'},
          {label:'Participated',      value:participated,              color:'var(--cyan)'},
          {label:'Awarded',           value:awarded,                   color:'var(--green)'},
          {label:`Closing in ${SOON_DAYS} days (click)`, value:soonCount, color:'var(--amber)', onClick:()=>setFSoon(v=>!v), active:fSoon},
          {label:'Total Tender Value',value:`₹${INR(totalValue)}`,     color:'var(--text2)'},
        ].map(s=>(
          <div key={s.label} className="stat-card" onClick={s.onClick} style={{cursor:s.onClick?'pointer':undefined,outline:s.active?'2px solid var(--amber)':undefined}}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{color:s.color,fontSize:String(s.value).startsWith('₹')?14:22}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:240}}>
            <Search/><input placeholder="Search work, tender ref, location..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select style={{width:100,fontSize:12,padding:'4px 6px'}} value={fOrg} onChange={e=>setFOrg(e.target.value)}>
            <option value="">All Orgs</option>
            {(uniqueOrgs.length ? uniqueOrgs : ORGANIZATIONS).map(o=><option key={o} value={o}>{o}</option>)}
          </select>
          <select style={{width:120,fontSize:12,padding:'4px 6px'}} value={fDistrict} onChange={e=>setFDistrict(e.target.value)}>
            <option value="">All Districts</option>
            {(uniqueDists.length ? uniqueDists : DISTRICTS_LIST).map(d=><option key={d} value={d}>{d}</option>)}
          </select>
          <select style={{width:110,fontSize:12,padding:'4px 6px'}} value={fFund} onChange={e=>setFFund(e.target.value)}>
            <option value="">All Funds</option>
            {(uniqueFunds.length ? uniqueFunds : FUND_TYPES).map(f=><option key={f} value={f}>{f}</option>)}
          </select>
          <select style={{width:110,fontSize:12,padding:'4px 6px'}} value={fParticip} onChange={e=>setFParticip(e.target.value)}>
            <option value="">All Particip.</option>
            {PARTICIP_OPTS.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <select style={{width:120,fontSize:12,padding:'4px 6px'}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
            <option value="">All Status</option>
            {STATUS_OPTS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{width:80,fontSize:12,padding:'4px 6px'}} value={fYear} onChange={e=>setFYear(e.target.value)}>
            <option value="">All Years</option>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          {(search||fOrg||fDistrict||fFund||fParticip||fStatus||fYear||fSoon)&&
            <button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFOrg('');setFDistrict('');setFFund('');setFParticip('');setFStatus('');setFYear('');setFSoon(false);}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} tenders</span>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button className="btn btn-secondary" onClick={exportTenders}><Download size={14}/> Export Excel</button>
          {editable&&<button className="btn btn-secondary" onClick={()=>setShowImport(true)}><Upload size={14}/> Import Excel</button>}
          {editable&&<button className="btn btn-primary" onClick={openNew}><Plus size={14}/> Add Tender</button>}
        </div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              {th('tender_ref','Tender Ref')}
              {th('organization','Organization')}
              {th('district','District')}
              <th>Location</th>
              <th>Work Description</th>
              {th('nature_of_fund','Fund')}
              {th('tender_value','Tender Value (₹)',{textAlign:'right'})}
              {th('tender_published_date','Published')}
              {th('tender_closing_date','Closing')}
              {th('bid_opening_date','Bid Opening')}
              {th('tender_submitted_date','Submitted')}
              <th>Completion</th>
              {th('participation','Participation')}
              {th('tender_status','Status')}
              <th style={{textAlign:'right'}}>Form Fee (₹)</th>
              <th style={{textAlign:'right'}}>EMD Fee (₹)</th>
              <th style={{textAlign:'right'}}>Security (₹)</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length===0?(
              <tr><td colSpan={COLS}><div className="empty-state"><FileText size={32}/><h3>No tenders found</h3></div></td></tr>
            ):shown.map((r,i)=>{
              const overdueOpen = r.tender_closing_date && r.tender_closing_date < today && r.tender_status==='Open';
              const soon = closingSoon(r);
              return (
              <tr key={r.id}>
                <td style={{fontSize:11,color:'var(--text3)'}}>{cur*PAGE_SIZE+i+1}</td>
                <td style={{fontSize:11,fontFamily:'monospace',fontWeight:600,minWidth:130,whiteSpace:'nowrap'}}>{r.tender_ref||'—'}</td>
                <td><span className="badge badge-blue" style={{fontSize:9}}>{r.organization||'—'}</span></td>
                <td style={{fontSize:11}}>{r.district||'—'}</td>
                <td style={{fontSize:11,maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.project_location}>{r.project_location||'—'}</td>
                <td style={{fontSize:11,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:500,cursor:'pointer'}} title={r.work_description} onClick={()=>setShowView(r)}>{r.work_description}</td>
                <td style={{fontSize:11}}>{r.nature_of_fund||'—'}</td>
                <td style={{textAlign:'right',fontWeight:700,fontSize:13,color:'var(--accent)'}}>{money(r.tender_value)}</td>
                <td style={{fontSize:11,whiteSpace:'nowrap'}}>{fmtDMY(r.tender_published_date)||'—'}</td>
                <td style={{fontSize:11,whiteSpace:'nowrap',color:overdueOpen?'var(--red)':soon?'var(--amber)':undefined,fontWeight:(overdueOpen||soon)?700:undefined}}
                    title={overdueOpen?'Closing date passed but status is still Open':soon?'Closing soon':undefined}>{fmtDMY(r.tender_closing_date)||'—'}</td>
                <td style={{fontSize:11,whiteSpace:'nowrap'}}>{fmtDMY(r.bid_opening_date)||'—'}</td>
                <td style={{fontSize:11,whiteSpace:'nowrap'}}>{fmtDMY(r.tender_submitted_date)||'—'}</td>
                <td style={{fontSize:11,whiteSpace:'nowrap'}}>{r.work_completion||'—'}</td>
                <td>{participBadge(r.participation)}</td>
                <td>{statusBadge(r.tender_status)}</td>
                <td style={{textAlign:'right',fontSize:12}}>{money(r.tender_form_fee ?? r.tender_fee)}</td>
                <td style={{textAlign:'right',fontSize:12}}>{money(r.emd_fee ?? r.emd)}</td>
                <td style={{textAlign:'right',fontSize:12}}>{money(r.security_amount)}</td>
                <td style={{fontSize:10,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.remarks}>{r.remarks||'—'}</td>
                <td>
                  <div style={{display:'flex',gap:4}}>
                    <button className="btn btn-ghost btn-sm" title="View" onClick={()=>setShowView(r)}><Eye size={13}/></button>
                    {canConvert&&(r.tender_status==='Awarded'||r.participation==='Awarded')&&<button className="btn btn-ghost btn-sm" title="Create project from this tender" style={{color:'var(--green)'}} onClick={()=>convert(r)}><ArrowRight size={13}/></button>}
                    {editable&&<button className="btn btn-ghost btn-sm" title="Edit" onClick={()=>openEdit(r)}><Edit2 size={13}/></button>}
                    {editable&&<button className="btn btn-ghost btn-sm" title="Delete" style={{color:'var(--red)'}} onClick={()=>removeTender(r)}><Trash2 size={13}/></button>}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      {sorted.length>PAGE_SIZE&&(
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,marginTop:12,fontSize:12}}>
          <button className="btn btn-secondary btn-sm" disabled={cur===0} onClick={()=>setPage(0)}>« First</button>
          <button className="btn btn-secondary btn-sm" disabled={cur===0} onClick={()=>setPage(cur-1)}>‹ Prev</button>
          <span>Page {cur+1} of {pages} · rows {cur*PAGE_SIZE+1}–{Math.min(sorted.length,(cur+1)*PAGE_SIZE)} of {sorted.length}</span>
          <button className="btn btn-secondary btn-sm" disabled={cur>=pages-1} onClick={()=>setPage(cur+1)}>Next ›</button>
          <button className="btn btn-secondary btn-sm" disabled={cur>=pages-1} onClick={()=>setPage(pages-1)}>Last »</button>
        </div>
      )}

      {showView&&<TenderView r={showView} editable={editable} canConvert={canConvert}
        onClose={()=>setShowView(null)} onEdit={openEdit} onConvert={convert}/>}

      {/* ADD / EDIT MODAL */}
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal modal-lg" style={{maxWidth:820,maxHeight:'93vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Tender</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">

              <SH title="A — Tender Information" icon={FileText} mt={0}/>
              <div className="form-grid">
                <FG label="Tender Reference Number">
                  <input value={form.tender_ref} onChange={e=>sf('tender_ref',e.target.value)} placeholder="e.g. 01/EE/LSGD/2022-2023" style={{fontFamily:'monospace'}}/>
                </FG>
                <FG label="Organization">
                  <select value={form.organization} onChange={e=>sf('organization',e.target.value)}>
                    <option value="">Select</option>
                    {ORGANIZATIONS.map(o=><option key={o} value={o}>{o}</option>)}
                    {form.organization&&!ORGANIZATIONS.includes(form.organization)&&<option value={form.organization}>{form.organization}</option>}
                  </select>
                </FG>
                <FG label="District">
                  <select value={form.district} onChange={e=>sf('district',e.target.value)}>
                    <option value="">Select District</option>
                    {DISTRICTS_LIST.map(d=><option key={d} value={d}>{d}</option>)}
                    {form.district&&!DISTRICTS_LIST.includes(form.district)&&<option value={form.district}>{form.district}</option>}
                  </select>
                </FG>
                <FG label="Project Location">
                  <input value={form.project_location} onChange={e=>sf('project_location',e.target.value)} placeholder="e.g. Kakkanad, Sooranad South"/>
                </FG>
                <FG label="Nature of Fund">
                  <select value={form.nature_of_fund} onChange={e=>sf('nature_of_fund',e.target.value)}>
                    <option value="">Select Fund</option>
                    {FUND_TYPES.map(f=><option key={f} value={f}>{f}</option>)}
                    {form.nature_of_fund&&!FUND_TYPES.includes(form.nature_of_fund)&&<option value={form.nature_of_fund}>{form.nature_of_fund}</option>}
                  </select>
                </FG>
                <FG label="Tender Value (₹)">
                  <input type="number" min="0" value={form.tender_value} onChange={e=>sf('tender_value',e.target.value)} placeholder="0.00"/>
                </FG>
                <FG label="Work Description *" full>
                  <textarea rows={3} value={form.work_description} onChange={e=>sf('work_description',e.target.value)} placeholder="Full description of the work..."/>
                </FG>
              </div>

              <SH title="B — Key Dates" color="#0891b2" icon={Calendar}/>
              <div className="form-grid">
                <FG label="Tender Published Date">
                  <DateInput value={form.tender_published_date} onChange={e=>sf('tender_published_date',e.target.value)}/>
                </FG>
                <FG label="Tender Closing Date">
                  <DateInput value={form.tender_closing_date} onChange={e=>sf('tender_closing_date',e.target.value)}/>
                </FG>
                <FG label="Bid Opening Date">
                  <DateInput value={form.bid_opening_date} onChange={e=>sf('bid_opening_date',e.target.value)}/>
                </FG>
                <FG label="Work Completion Period">
                  <input value={form.work_completion} onChange={e=>sf('work_completion',e.target.value)} placeholder="e.g. 180 DAYS, 300 days"/>
                </FG>
              </div>

              <SH title="C — Participation & Status" color="#16a34a"/>
              <div className="form-grid">
                <FG label="Tender Participation by Armstech">
                  <select value={form.participation} onChange={e=>sf('participation',e.target.value)}>
                    {PARTICIP_OPTS.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </FG>
                {form.participation !== 'NO' && (
                  <FG label="Tender Submitted Date">
                    <DateInput value={form.tender_submitted_date} onChange={e=>sf('tender_submitted_date',e.target.value)}/>
                  </FG>
                )}
                <FG label="Tender Status">
                  <select value={form.tender_status} onChange={e=>sf('tender_status',e.target.value)}>
                    <option value="">Select Status</option>
                    {STATUS_OPTS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </FG>
              </div>

              <SH title="D — Financial Details" color="#d97706" icon={DollarSign}/>
              <div className="form-grid">
                <FG label="Tender Form Fee (₹)">
                  <input type="number" min="0" value={form.tender_form_fee} onChange={e=>sf('tender_form_fee',e.target.value)} placeholder="0.00"/>
                </FG>
                <FG label="EMD Fee (₹)">
                  <input type="number" min="0" value={form.emd_fee} onChange={e=>sf('emd_fee',e.target.value)} placeholder="0.00"/>
                </FG>
                <FG label="Security Amount (₹)">
                  <input type="number" min="0" value={form.security_amount} onChange={e=>sf('security_amount',e.target.value)} placeholder="0.00"/>
                </FG>
              </div>

              <SH title="E — Remarks" color="#dc2626"/>
              <div className="form-grid">
                <FG label="Remarks" full>
                  <textarea rows={3} value={form.remarks} onChange={e=>sf('remarks',e.target.value)} placeholder="Full tender details, additional notes..."/>
                </FG>
              </div>

              <SH title="F — Attachments (tender document, BOQ, work order…)" color="#475569" icon={Paperclip}/>
              <Attachments value={form.attachments} onChange={v=>sf('attachments',v)} folder={`tenders/${uploadKey}`} editable/>

            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* IMPORT MODAL */}
      {showImport&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&!importing&&setShowImport(false)}>
          <div className="modal" style={{maxWidth:540}}>
            <div className="modal-header">
              <span className="modal-title">📥 Import Tenders from Excel</span>
              <button className="btn btn-ghost btn-sm" disabled={importing} onClick={()=>setShowImport(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:10,padding:14,marginBottom:16}}>
                <div style={{fontWeight:700,marginBottom:8}}>Supported column format (matches your existing Excel):</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,fontSize:12}}>
                  {['Tender Ref','Tender Fee / Tender Form Fee','EMD / EMD Fee','Organization','District','Project Location',
                    'Work Description','Nature of Fund','Tender Value','Tender Published date',
                    'Tender closing date','Bid Opening Date','Tender Submitted Date','Work completion',
                    'Tender Participation by Armstech','Tender status',
                    'Security Amount','Remarks'].map(col=>(
                    <div key={col} style={{background:'rgba(255,255,255,0.5)',borderRadius:4,padding:'3px 8px',fontSize:11,color:'#0f172a'}}>{col}</div>
                  ))}
                </div>
                <div style={{fontSize:11,marginTop:8,color:'var(--text2)'}}>Tenders already in the database (same reference, or same description + date + value when there is no reference) are skipped.</div>
              </div>
              <div style={{border:'2px dashed var(--border)',borderRadius:10,padding:30,textAlign:'center',cursor:importing?'wait':'pointer'}}
                onClick={()=>!importing&&fileRef.current?.click()}>
                <Upload size={32} style={{color:'var(--accent)',marginBottom:8}}/>
                <div style={{fontWeight:600,marginBottom:4}}>{importing?'Importing… please wait':'Click to upload Excel or CSV'}</div>
                <div style={{fontSize:12,color:'var(--text3)'}}>Your existing TENDER_DB format is supported</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleImport}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" disabled={importing} onClick={()=>setShowImport(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
